import React, { useEffect, useState } from "react";
import Button from "Components/button/Button";
import t from "Utils/translation";
import RadioButton from "Components/radiobutton/RadioButton";
import DeliveryVolumeSelection from "Components/deliveryVolumeSelection/DeliveryVolumeSelection";
import { deliveryPlans } from "@/config";
import formatCurrency from "Utils/formatCurrency";
import { Icon, arrowRight } from "Icons/index";
import WarningBanner from "Components/warningBanner/WarningBanner";
import XDate from "xdate";
import ConfirmModal from "Components/confirmModal/ConfirmModal";
import compact from "lodash/compact";

const formatter = new Intl.NumberFormat(navigator.language || navigator.userLanguage, {
  maximumFractionDigits: 0,
});

const calcNewExpiration = () => {
  const date = new XDate(new Date(), true);

  date.addYears(1);

  // Format the date back to a string in 'dd/mm/yyyy' format
  return date.toString("dd/MM/yyyy");
};

export default function PlanForm({
  setSelectedTier,
  selectedTier,
  formData,
  setFormFieldValue,
  onFormCancel,
  nextStep,
  stepper,
  isUpdating,
  prices,
  accountData,
  canHaveFreeAccount,
  hideFreeAccountPicker,
  isEditPlanVolume,
  isChangePlan,
  hidePrices,
  isExtradelivery,
  isReseller,
  isLoggedUserAdmin,
}) {
  const validateAndNext = async (e) => {
    e.preventDefault();

    if (isSameTier) {
      setSamePlanConfirm(true);
      return;
    }

    setFormFieldValue({ product_id: selectedProductId });

    nextStep();
  };

  const [samePlanConfirm, setSamePlanConfirm] = useState(false);
  const [planQuotas, setPlanQuotas] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [currentQuota, setCurrentQuota] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [resellerDiscountedPrice, setResellerDiscountedPrice] = useState(null);
  const [isSameTier, setIsSameTier] = useState(true);
  const [sliderTocuhed, setSliderTouched] = useState(false);

  const [changeBreakdown, setChangeBreakdown] = useState(null);

  useEffect(() => {
    if (isExtradelivery) return;

    const pricing = prices[formData.type];
    const quotas = pricing[formData.plan].map((p) => p.quota);

    if (!sliderTocuhed && isUpdating) {
      // set right value on slider only first render
      const currentQuota = quotas.indexOf(accountData.num_messages);
      if (currentQuota > -1) {
        setFormFieldValue({ volume: currentQuota });
      }
    }

    setPlanQuotas(quotas.map((q) => formatter.format(q)));
    calculatePrice(quotas);
  }, [formData.type, formData.plan, prices, formData.volume]);

  useEffect(() => {
    if (
      isUpdating &&
      accountData &&
      selectedTier &&
      selectedTier.paytype !== "prepaid" && // if prepaid it can buy the same package because the quantity will be added
      accountData.num_messages == selectedTier.quota &&
      accountData.plan == selectedTier.paytype
    ) {
      setIsSameTier(true);
      setChangeBreakdown(null);
    } else {
      setIsSameTier(false);
      if (accountData && selectedTier) {
        setChangeBreakdown({
          old: { quota: accountData.num_messages, paytype: accountData.plan, free: accountData.free },
          new: { quota: selectedTier.quota, paytype: selectedTier.paytype, free: selectedTier.free },
        });
      }
    }
  }, [selectedTier]);

  const calculatePrice = (quotas) => {
    const q = quotas || planQuotas;
    const currentQuota = q[formData.volume];

    const currentTier = prices[formData.type][formData.plan].find((p) => p.quota === currentQuota);
    if (!currentTier) {
      setCurrentPrice(null);
      setResellerDiscountedPrice(null);
      setCurrentQuota(null);
      if (setSelectedTier) setSelectedTier(null);
      return;
    }

    if (setSelectedTier) setSelectedTier(currentTier);

    const formattedPrice = formatCurrency(currentTier.pricing_eur);
    setCurrentQuota(currentTier.quota);

    const resellerTier = currentTier.special_tiers?.find((t) => t.name === "Reseller");
    if (resellerTier) {
      const formattedResellerPrice = formatCurrency(resellerTier.pricing_eur);
      setResellerDiscountedPrice(formattedResellerPrice);
    } else {
      setResellerDiscountedPrice(null);
    }

    setSelectedProductId(currentTier.id);
    setCurrentPrice(formattedPrice);
  };

  const getInfoMsg = () => {
    if (formData.plan === deliveryPlans.MONTHLY) {
      return t("delivery.accounts.add.stepPlan.volume.msg");
    } else if (formData.plan === deliveryPlans.PREPAID) {
      return t("delivery.accounts.add.stepPlan.prepaid.volume.msg");
    }

    return null;
  };

  const planRadioChildren = compact([
    !hideFreeAccountPicker && {
      value: "free",
      label: t("delivery.accounts.add.stepPlan.plan.free.label"),
      disabled: !canHaveFreeAccount,
      description: isChangePlan
        ? null
        : !canHaveFreeAccount
          ? isLoggedUserAdmin
            ? "Ha già raggiunto il limite di account gratuiti. Aumentali nella impostazioni del cliente per procedere."
            : "Hai già un account gratuito, puoi averne solo uno per volta."
          : null,
    },
    {
      value: "monthly",
      disabled: isLoggedUserAdmin,
      label: isLoggedUserAdmin
        ? "Admin può creare solo account FREE"
        : t("delivery.accounts.add.stepPlan.plan.monthly.label"),
    },
    {
      value: "prepaid",
      disabled: isLoggedUserAdmin,
      label: isLoggedUserAdmin
        ? "Admin può creare solo account FREE"
        : t("delivery.accounts.add.stepPlan.plan.prepaid.label"),
    },
  ]);

  let stepTitle = t("delivery.accounts.add.stepPlan.title");
  if (isEditPlanVolume) {
    stepTitle =
      accountData.plan === "monthly"
        ? t("delivery.accounts.update.stepPlanModification.title.monthly")
        : t("delivery.accounts.update.stepPlanModification.title.prepaid");
  } else if (isChangePlan) {
    stepTitle = t("delivery.accounts.update.stepPlanModification.title");
  }

  let stepDesc = t("delivery.accounts.add.stepPlan.desc");
  if (isEditPlanVolume) {
    stepDesc =
      accountData.plan === "monthly"
        ? t("delivery.accounts.update.stepPlanModification.desc.monthly")
        : t("delivery.accounts.update.stepPlanModification.desc.prepaid");
  } else if (isChangePlan) {
    stepDesc = t("delivery.accounts.update.stepPlanModification.desc");
  }

  return (
    <div className="screenContainer flexAlignCenter">
      {stepper}
      <div className="mt-4 w-full max-w-[--form-page-width]">
        <div className="sectionTitle">{stepTitle}</div>
        <div className="sectionDesc">{stepDesc}</div>
        <Button docsKey="deliveryAccountPlan" className="btnDocsFP" color="docs" sizeSmall>
          button.docs
        </Button>
      </div>

      <div className="formContainer">
        <div className="flexColumnContainer">
          <div className="mt-9">
            {!isEditPlanVolume && (
              <>
                <div className="inputFull2">
                  <div className="settingTitle">
                    {isChangePlan
                      ? t("delivery.accounts.update.stepPlanModification.plan.title")
                      : t("delivery.accounts.add.stepPlan.plan.title")}
                  </div>
                  <div className="sectionInfo">{t("delivery.accounts.add.stepPlan.plan.msg")}</div>
                </div>
                <div className="mt-2">
                  <RadioButton
                    // eslint-disable-next-line react/no-children-prop
                    children={planRadioChildren}
                    name="plan"
                    value={formData.plan}
                    onChange={(v) => {
                      setFormFieldValue({ ...v, volume: 0 });
                    }}
                  />
                </div>
              </>
            )}
          </div>

          <DeliveryVolumeSelection
            plan={formData.plan}
            showSlider={!(formData.plan === deliveryPlans.FREE)}
            volumeValue={currentQuota}
            priceValue={currentPrice}
            hidePrices={hidePrices}
            resellerPriceValue={resellerDiscountedPrice}
            vatMsg={t("infoMsg.noVat")}
            onChange={(v) => {
              setSliderTouched(true);
              setFormFieldValue(v);
            }}
            isExtradelivery={isExtradelivery}
            formValue={formData.volume}
            className="mt-9"
            name="volume"
            sliderMarks={planQuotas}
            title={t("delivery.accounts.add.stepPlan.volume.title")}
            info={getInfoMsg()}
            isReseller={isReseller}
          />

          <ConfirmModal
            title={t("confirmationModal.title.delivery.samePlan")}
            text={t("confirmationModal.text.delivery.samePlan")}
            textButton="button.ok"
            hideCancelButton={true}
            opened={samePlanConfirm}
            action={() => setSamePlanConfirm(false)}
            handleClose={() => setSamePlanConfirm(false)}
            isWarning={true}
          />

          {isUpdating && !isSameTier && selectedTier && (
            <>
              <div className="mt-10 settingTitle">{t("delivery.accounts.update.stepPlanModification.change")}</div>
              <div className="flex items-center justify-between gap-1 pb-4">
                <div className="flex-1 p-2 border border-[--bg-d5] border-solid rounded flex flex-col items-center gap-1">
                  <div className="mb-4 text-xs font-semibold uppercase">
                    {accountData.plan === "prepaid"
                      ? t("delivery.accounts.add.stepPlan.changeRecapCreditNow")
                      : t("delivery.accounts.add.stepPlan.changeRecapTitleNow")}
                  </div>

                  <div className="text-sm">{t(`delivery.accounts.add.stepPlan.plan.${accountData.plan}.label`)}</div>
                  <div className="text-lg font-semibold">{formatter.format(accountData.num_messages)}</div>

                  {accountData.plan === "prepaid" && changeBreakdown?.new.paytype === "prepaid" && (
                    <div className="text-xs">
                      {t("delivery.accounts.update.stepPlanModification.expiration")}
                      {accountData.plan_expiration_date}
                    </div>
                  )}
                </div>

                <Icon svg={arrowRight} height={"8px"} />
                <div className="flex-1 p-2 border border-[--bg-d5] border-solid rounded flex flex-col items-center gap-1">
                  <div className="mb-4 text-xs font-semibold uppercase">
                    {accountData.plan === "prepaid" && changeBreakdown?.new.paytype === "prepaid"
                      ? t("delivery.accounts.add.stepPlan.changeRecapCreditNext")
                      : t("delivery.accounts.add.stepPlan.changeRecapTitleNext")}
                  </div>

                  <div className="text-sm">{t(`delivery.accounts.add.stepPlan.plan.${formData.plan}.label`)}</div>
                  <div className="text-lg font-semibold">
                    {accountData.plan === "prepaid" && changeBreakdown?.new.paytype === "prepaid"
                      ? formatter.format(accountData.num_messages + changeBreakdown?.new.quota)
                      : formatter.format(selectedTier.quota)}
                  </div>

                  {accountData.plan === "prepaid" && changeBreakdown?.new.paytype === "prepaid" && (
                    <div className="text-xs">
                      {t("delivery.accounts.update.stepPlanModification.expiration")} {calcNewExpiration()}
                    </div>
                  )}
                </div>
              </div>
              {/* <div className="text-xs mb-4 text-[--text-a60]">{t("delivery.accounts.add.stepPlan.disclaimer")}</div> */}

              {changeBreakdown?.old.paytype === "monthly" && changeBreakdown?.new.paytype === "prepaid" ? (
                <WarningBanner
                  msg={t("delivery.accounts.add.stepPlan.changeDescMonthToPrepaid")}
                  fullWidth
                  color="yellow"
                />
              ) : changeBreakdown?.old.paytype === "prepaid" && changeBreakdown?.new.paytype === "prepaid" ? (
                <WarningBanner
                  msg={t("delivery.accounts.add.stepPlan.changeDescPrepaid")}
                  fullWidth
                  className="mt-4"
                  color="yellow"
                />
              ) : changeBreakdown?.old.paytype === "prepaid" && changeBreakdown?.new.paytype === "monthly" ? (
                <WarningBanner
                  msg={t("delivery.accounts.add.stepPlan.changeDescPrepaidToMonth")}
                  className="mt-4"
                  fullWidth
                  color="yellow"
                />
              ) : (
                <WarningBanner
                  msg={t("delivery.accounts.add.stepPlan.changeDesc")}
                  className="mt-4"
                  fullWidth
                  color="yellow"
                />
              )}
            </>
          )}
        </div>
        <div className="flexColumnContainer">
          {formData.plan === deliveryPlans.PREPAID && (
            <div className="text-xs text-[--text-a60]">{t("delivery.accounts.add.stepPlan.footerMsg")}</div>
          )}
          <div className="formFooter">
            <Button color="text" type="button" onClick={onFormCancel}>
              button.cancel
            </Button>
            <Button disabled={isLoggedUserAdmin && !canHaveFreeAccount} onClick={validateAndNext} color="accent">
              button.continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
